import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Paperclip, Download, FileText, Image, File, Film, Music, Archive, ChevronDown, ChevronUp, Eye, Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ProjectAttachments = ({ projectId, tasks = [] }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewType, setPreviewType] = useState('');
  
  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [showUploadArea, setShowUploadArea] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    fetchAllAttachments();
  }, [projectId, tasks]);

  const fetchAllAttachments = () => {
    try {
      setLoading(true);
      const allAttachments = [];
      
      for (const task of tasks) {
        if (task.attachments && task.attachments.length > 0) {
          task.attachments.forEach(att => {
            allAttachments.push({
              ...att,
              taskId: task.id,
              taskTitle: task.title
            });
          });
        }
      }
      
      setAttachments(allAttachments);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setShowUploadArea(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    if (!selectedTaskId) {
      toast.error('Bitte wähle zuerst einen Task aus');
      return;
    }

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu gross. Maximum: 10 MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/tasks/${selectedTaskId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Datei erfolgreich hochgeladen');
      // Refresh by triggering parent to refetch
      window.location.reload(); // Simple reload to refresh task data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isImage = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  };

  const isPDF = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ext === 'pdf';
  };

  const isPreviewable = (filename) => {
    return isImage(filename) || isPDF(filename);
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    
    if (isImage(filename)) return <Image className="w-4 h-4 text-green-500" />;
    if (isPDF(filename)) return <FileText className="w-4 h-4 text-red-500" />;
    if (['mp4', 'avi', 'mov', 'webm'].includes(ext)) return <Film className="w-4 h-4 text-purple-500" />;
    if (['mp3', 'wav', 'ogg', 'flac'].includes(ext)) return <Music className="w-4 h-4 text-pink-500" />;
    if (['zip', 'rar', 'tar', 'gz', '7z'].includes(ext)) return <Archive className="w-4 h-4 text-yellow-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="w-4 h-4 text-blue-500" />;
    if (['xls', 'xlsx'].includes(ext)) return <FileText className="w-4 h-4 text-green-600" />;
    
    return <File className="w-4 h-4 text-muted-foreground" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await axios.get(`${API}/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename || attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handlePreview = async (attachment) => {
    const filename = attachment.original_filename || attachment.filename;
    try {
      const response = await axios.get(`${API}/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: attachment.content_type });
      const url = window.URL.createObjectURL(blob);
      
      setPreviewUrl(url);
      setPreviewFilename(filename);
      setPreviewType(isImage(filename) ? 'image' : 'pdf');
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const displayedAttachments = isExpanded ? attachments : attachments.slice(0, 3);

  return (
    <>
      <Card 
        data-testid="project-attachments-card"
        onDragEnter={handleDragEnter}
        className={`transition-colors ${isDragging ? 'ring-2 ring-primary ring-offset-2' : ''}`}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              Anhänge
              {attachments.length > 0 && (
                <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
                  {attachments.length}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setShowUploadArea(!showUploadArea)}
            >
              <Upload className="w-3 h-3 mr-1" />
              Hochladen
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Upload Area */}
          {showUploadArea && (
            <div 
              ref={dropZoneRef}
              className={`mb-3 p-3 border-2 border-dashed rounded-lg transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Task Selection */}
              <div className="mb-2">
                <label className="text-xs text-muted-foreground mb-1 block">Task auswählen:</label>
                <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Task auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tasks.map(task => (
                      <SelectItem key={task.id} value={task.id} className="text-xs">
                        {task.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Drop Zone */}
              <div 
                className={`flex flex-col items-center justify-center py-4 text-center cursor-pointer ${
                  !selectedTaskId ? 'opacity-50' : ''
                }`}
                onClick={() => selectedTaskId && fileInputRef.current?.click()}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin text-primary mb-2" />
                    <p className="text-xs text-muted-foreground">Wird hochgeladen...</p>
                  </>
                ) : (
                  <>
                    <Upload className={`w-6 h-6 mb-2 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="text-xs text-muted-foreground">
                      {isDragging ? 'Datei hier ablegen' : 'Datei hierher ziehen oder klicken'}
                    </p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1">
                      Max. 10 MB
                    </p>
                  </>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                disabled={!selectedTaskId || uploading}
              />
            </div>
          )}

          {/* Attachments List */}
          {loading ? (
            <div className="text-xs text-muted-foreground text-center py-4">
              Lädt...
            </div>
          ) : attachments.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-4">
              Keine Anhänge vorhanden
            </div>
          ) : (
            <div className="space-y-2">
              {displayedAttachments.map((attachment, index) => {
                const filename = attachment.original_filename || attachment.filename;
                const previewable = isPreviewable(filename);
                
                return (
                  <div 
                    key={attachment.id || index}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                  >
                    <div 
                      className={`flex items-center gap-2 min-w-0 flex-1 ${previewable ? 'cursor-pointer' : ''}`}
                      onClick={() => previewable && handlePreview(attachment)}
                    >
                      {getFileIcon(filename)}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate" title={filename}>
                          {filename}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate" title={attachment.taskTitle}>
                          {attachment.taskTitle}
                          {attachment.file_size && ` • ${formatFileSize(attachment.file_size)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 ml-1">
                      {previewable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handlePreview(attachment)}
                          title="Vorschau"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDownload(attachment)}
                        title="Herunterladen"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              
              {attachments.length > 3 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs h-7"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3 mr-1" />
                      Weniger anzeigen
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3 mr-1" />
                      {attachments.length - 3} weitere anzeigen
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <span className="truncate">{previewFilename}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center overflow-auto max-h-[70vh]">
            {previewType === 'image' && previewUrl && (
              <img 
                src={previewUrl} 
                alt={previewFilename} 
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            )}
            {previewType === 'pdf' && previewUrl && (
              <iframe 
                src={previewUrl} 
                title={previewFilename}
                className="w-full h-[70vh] rounded"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectAttachments;
